import { Component, computed, input, output, ChangeDetectionStrategy } from '@angular/core';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiDialogModule, OuiDialogService } from 'omnium-ui/dialog';
import { OuiTagModule } from 'omnium-ui/tag';
import { ActionAuthorizationService, doublonsBusterActionCodes } from '../../../../../../core/auth/action-authorization.service';
import { ConsultantCardComponent } from '../../../../../../core/components/consultant-card/consultant-card.component';
import { InvestorCardComponent } from '../../../../../../core/components/investor-card/investor-card.component';
import {
  InvestorViewerPerson,
  ViewerCoupleRelation,
  ViewerFamilyRelation,
  ViewerPersonSummary,
  ViewerOtherRelation,
} from '../../../../../../core/models/api.models';
import {
  formatAddress,
  formatBoolean,
  formatDate,
  formatIdentity,
  formatNotes,
  formatPersonLabel,
  formatPhone,
  formatText,
} from '../../investor-viewer-format.utils';
import { InvestorViewerAccordionComponent } from '../investor-viewer-accordion/investor-viewer-accordion.component';
import { InvestorViewerPersonCorrectionDialogComponent } from '../investor-viewer-person-correction-dialog/investor-viewer-person-correction-dialog.component';
import { InvestorRelationDeletionDialogComponent } from '../investor-relation-deletion-dialog/investor-relation-deletion-dialog.component';
import { OuiIconModule } from "omnium-ui/icon";
import { OuiTooltipModule } from "omnium-ui/tooltip";

type ViewerRelation = ViewerCoupleRelation | ViewerFamilyRelation | ViewerOtherRelation;

@Component({
  selector: 'dbw-investor-viewer-persons-section',
  imports: [
    OuiButtonModule,
    OuiDialogModule,
    OuiTagModule,
    ConsultantCardComponent,
    InvestorCardComponent,
    InvestorViewerAccordionComponent,
    OuiIconModule,
    OuiTooltipModule
],
  templateUrl: './investor-viewer-persons-section.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../../investor-viewer-section.shared.scss',
})
export class InvestorViewerPersonsSectionComponent {
  readonly investorId = input.required<number>();
  readonly currentConsultantId = input<number | null | undefined>(undefined);
  readonly summaries = input.required<ViewerPersonSummary[]>();
  readonly persons = input.required<InvestorViewerPerson[]>();
  readonly loading = input(false);
  readonly error = input<string | null>(null);
  readonly correctionExecuted = output<void>();
  readonly relationDeleted = output<void>();
  readonly canExecutePersonsCorrectionAction = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.investorPersonsCorrectionExecute)
  );
  readonly canExecuteRelationDeletionAction = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.investorRelationDeletionExecute)
  );

  protected readonly formatText = formatText;
  protected readonly formatBoolean = formatBoolean;
  protected readonly formatDate = formatDate;
  protected readonly formatNotes = formatNotes;
  protected readonly formatPersonLabel = formatPersonLabel;
  protected readonly formatAddress = formatAddress;
  protected readonly formatPhone = formatPhone;

  constructor(
    private readonly dialogService: OuiDialogService,
    private readonly actionAuthorizationService: ActionAuthorizationService
  ) {}

  protected openCorrectionDialog(): void {
    if (
      !this.canExecutePersonsCorrectionAction() ||
      this.loading() ||
      this.persons().length <= 1 ||
      this.persons().length !== this.summaries().length
    ) {
      return;
    }

    this.dialogService
      .openDialog(
        InvestorViewerPersonCorrectionDialogComponent,
        {
          investorId: this.investorId(),
          persons: this.persons(),
        },
        undefined,
        '1100px'
      )
      .afterClosed()
      .subscribe(result => {
        if (result?.refreshInspection === true) {
          this.correctionExecuted.emit();
        }
      });
  }

  protected openRelationDeletionDialog(
    person: InvestorViewerPerson,
    relation: ViewerFamilyRelation | ViewerOtherRelation,
    relationKind: 'famille' | 'autre'
  ): void {
    if (!this.canExecuteRelationDeletionAction() || this.loading()) {
      return;
    }

    const relationId =
      relationKind === 'famille'
        ? (relation as ViewerFamilyRelation).familyRelationId
        : (relation as ViewerOtherRelation).otherRelationId;

    this.openRelationDeletionDialogInternal(person, relation, relationKind, relationId);
  }

  protected openCoupleRelationDeletionDialog(person: InvestorViewerPerson, relation: ViewerCoupleRelation): void {
    if (!this.canExecuteRelationDeletionAction() || this.loading() || !this.hasRelatedInvestor(relation) || !relation.relatedPersonId) {
      return;
    }

    this.openRelationDeletionDialogInternal(
      person,
      relation,
      'couple',
      relation.relatedInvestorId,
      relation.relatedPersonId
    );
  }

  private openRelationDeletionDialogInternal(
    person: InvestorViewerPerson,
    relation: ViewerRelation,
    relationKind: 'famille' | 'autre' | 'couple',
    relationId: number,
    relatedPersonId?: number
  ): void {
    this.dialogService
      .openDialog(
        InvestorRelationDeletionDialogComponent,
        {
          investorId: this.investorId(),
          personId: person.personId,
          personLabel: formatPersonLabel(person),
          relationId,
          relatedPersonId,
          relationKind,
          relationTypeLabel: this.getRelationTypeLabel(relation),
          relatedEntityLabel: this.getRelationDeletionRelatedEntityLabel(relation),
        },
        undefined,
        '600px'
      )
      .afterClosed()
      .subscribe(result => {
        if (result?.relationDeleted === true) {
          this.relationDeleted.emit();
        }
      });
  }

  protected get loadedPersonCount(): number {
    return this.persons().length;
  }

  protected getPersonRelationCount(person: InvestorViewerPerson): number {
    return person.coupleRelations.length + person.familyRelations.length + person.otherRelations.length;
  }

  protected getPersonContactCount(person: InvestorViewerPerson): number {
    return person.addresses.length + person.phones.length + person.emails.length;
  }

  protected getIdentitySummary(person: InvestorViewerPerson): string {
    return (
      [
        person.birthDate ? `Naissance ${formatDate(person.birthDate)}` : null,
        person.birthCity ? formatText(person.birthCity) : null,
        person.nationalityCode ? `Nationalité ${formatText(person.nationalityCode)}` : null,
      ]
        .filter((item): item is string => !!item)
        .join(' • ') || 'Identité non renseignée'
    );
  }

  protected getCivilStatusSummary(person: InvestorViewerPerson): string {
    return (
      [
        person.maritalStatusCode ? `Situation ${formatText(person.maritalStatusCode)}` : null,
        person.matrimonialRegimeCode ? `Régime ${formatText(person.matrimonialRegimeCode)}` : null,
        person.protectionMeasureCode ? `Protection ${formatText(person.protectionMeasureCode)}` : null,
      ]
        .filter((item): item is string => !!item)
        .join(' • ') || 'État civil non renseigné'
    );
  }

  protected getDispositionSummary(person: InvestorViewerPerson): string {
    const activeDispositions = [
      person.hasDonationDernierVivant ? 'Donation dernier vivant' : null,
      person.hasDonationConjoint ? 'Donation conjoint' : null,
      person.hasDonationPartage ? 'Donation partage' : null,
      person.hasOtherDonations ? 'Autres donations' : null,
      person.hasLegsParticuliers ? 'Legs particuliers' : null,
      person.hasLegsUniversels ? 'Legs universels' : null,
    ].filter((item): item is string => !!item);

    return (
      activeDispositions.slice(0, 2).join(' • ') ||
      (activeDispositions.length > 0 ? `${activeDispositions.length} disposition(s)` : 'Aucune disposition active')
    );
  }

  protected getProfessionalSummary(person: InvestorViewerPerson): string {
    return (
      [
        person.profession ? formatText(person.profession) : null,
        person.companyName ? formatText(person.companyName) : null,
        person.socioProfessionalCategoryCode ? formatText(person.socioProfessionalCategoryCode) : null,
      ]
        .filter((item): item is string => !!item)
        .join(' • ') || 'Situation professionnelle non renseignée'
    );
  }

  protected getContactSummary(person: InvestorViewerPerson): string {
    return `${person.addresses.length} adresse(s) • ${person.phones.length} téléphone(s) • ${person.emails.length} email(s)`;
  }

  protected getRegulatorySummary(person: InvestorViewerPerson): string {
    return [
      person.fatcaResidenceCountryCode ? `FATCA ${formatText(person.fatcaResidenceCountryCode)}` : null,
      `US: ${formatBoolean(person.isUsCitizenOrResident)}`,
      `PPE: ${formatBoolean(person.isPoliticallyExposed)}`,
    ]
      .filter((item): item is string => !!item)
      .join(' • ');
  }

  protected getRelationsSummary(person: InvestorViewerPerson): string {
    return `${person.coupleRelations.length} couple • ${person.familyRelations.length} famille • ${person.otherRelations.length} autre(s)`;
  }

  protected getRegulatoryTagLabel(value: boolean | null | undefined, trueLabel: string, falseLabel: string): string {
    if (value === null || value === undefined) {
      return '-';
    }

    return value ? trueLabel : falseLabel;
  }

  protected getRegulatoryTagStatus(
    value: boolean | null | undefined,
    positiveStatus: 'completed' | 'pending' | 'error'
  ): 'completed' | 'pending' | 'error' {
    if (value === null || value === undefined) {
      return 'pending';
    }

    return value ? positiveStatus : 'completed';
  }

  protected formatCoupleRelation(relation: ViewerCoupleRelation): string {
    return this.getRelationTypeLabel(relation);
  }

  protected formatFamilyRelation(relation: ViewerFamilyRelation): string {
    return this.getRelationTypeLabel(relation);
  }

  protected formatOtherRelation(relation: ViewerOtherRelation): string {
    return this.getRelationTypeLabel(relation);
  }

  protected hasRelatedInvestor(relation: ViewerRelation): relation is ViewerRelation & { relatedInvestorId: number } {
    return relation.relatedInvestorId !== null && relation.relatedInvestorId !== undefined;
  }

  protected hasCrossConsultantWarning(relation: ViewerRelation): boolean {
    const currentConsultantId = this.currentConsultantId();

    return !!currentConsultantId && !!relation.relatedConsultantId && relation.relatedConsultantId !== currentConsultantId;
  }

  protected getRelatedInvestorCode(relation: ViewerRelation): string {
    return relation.relatedInvestorCode?.trim() || '-';
  }

  protected getRelatedInvestorName(relation: ViewerRelation): string {
    if (relation.relatedInvestorId) {
      return this.getRelatedPersonLabel(relation, `Investisseur #${relation.relatedInvestorId}`);
    }

    return this.getRelatedPersonLabel(relation, 'Investisseur lié non renseigné');
  }

  protected getRelatedConsultantCode(relation: ViewerRelation): string {
    return relation.relatedConsultantCode?.trim() || '-';
  }

  protected getRelatedConsultantName(relation: ViewerRelation): string {
    return relation.relatedConsultantName?.trim() || '-';
  }

  protected getRelationTypeLabel(relation: ViewerRelation): string {
    return relation.relationTypeCode?.trim() || 'Relation';
  }

  protected shouldShowRelatedPersonLabel(relation: ViewerRelation): boolean {
    return !this.hasRelatedInvestor(relation);
  }

  protected getRelationReferenceItems(relation: ViewerRelation): string[] {
    const items: string[] = [];
    const relationArianeId = relation.arianeId;
    const relatedInvestorArianeId = relation.relatedInvestorArianeId;

    if (relationArianeId !== null && relationArianeId !== undefined) {
      items.push(`Relation ${relationArianeId}`);
    }

    if (relatedInvestorArianeId !== null && relatedInvestorArianeId !== undefined) {
      items.push(`Investisseur ${relatedInvestorArianeId}`);
    }

    return items;
  }

  protected getFamilyBadges(relation: ViewerFamilyRelation): string[] {
    const items: string[] = [];

    if (relation.isAdoptedChild) {
      items.push('Enfant adopte');
    }

    if (relation.isDependentChild) {
      items.push('A charge');
    }

    if (relation.isSharedCustody) {
      items.push('Garde alternee');
    }

    return items;
  }

  protected getRelatedPersonLabel(
    relation: ViewerRelation,
    fallback = relation.relatedPersonId ? `Personne #${relation.relatedPersonId}` : undefined
  ): string {
    const relatedPerson = relation.relatedPersonId
      ? this.persons().find(person => person.personId === relation.relatedPersonId)
      : undefined;

    return relatedPerson
      ? formatIdentity(relatedPerson.lastName, relatedPerson.firstName, fallback)
      : formatIdentity(relation.relatedLastName, relation.relatedFirstName, fallback);
  }

  private getRelationDeletionRelatedEntityLabel(relation: ViewerRelation): string {
    if (!this.hasRelatedInvestor(relation)) {
      return this.getRelatedPersonLabel(relation);
    }

    const code = this.getRelatedInvestorCode(relation);
    const reference = code === '-' ? `#${relation.relatedInvestorId}` : `${code} (#${relation.relatedInvestorId})`;
    return `${this.getRelatedInvestorName(relation)} - ${reference}`;
  }
}
